import { useMemo } from "react";
import { useIsProductAdvancedEditMode } from "./useIsProductAdvancedEditMode";
import { useStableCallback } from "./useStableCallback";
import { useLogicalSelectedPage, useSelectedSpread } from "./useSelection";
import { useDefinition } from "./useDefinition";
import { useTextZoneDashboard } from "./useTextZoneDashboard";

export function useAddTextZone() {
	const isProductWithAdvancedEditMode = useIsProductAdvancedEditMode();
	const dashboard = useTextZoneDashboard();

	const definition = useDefinition();
	const page = useLogicalSelectedPage();
	const spread = useSelectedSpread();

	const canAddTextZoneToDefinition = useMemo(() => {
		if (!isProductWithAdvancedEditMode) return false;
		return dashboard.canAddTextZoneToDefinition(definition);
	}, [dashboard, definition, isProductWithAdvancedEditMode]);

	const canAddTextZoneToSpread = useMemo(() => {
		if (!isProductWithAdvancedEditMode) return false;
		return (
			spread?.pageZones.some((spreadPage) => dashboard.canAddTextZoneToPage(spreadPage)) ??
			false
		);
	}, [dashboard, isProductWithAdvancedEditMode, spread]);

	/** Add a zone on the selected page or on the first editable page */
	const addTextZone = useStableCallback(() => {
		if (!canAddTextZoneToSpread) return undefined;
		const pagesToCheck = [page, ...(spread ? spread.pageZones : [])].distinct();
		const addToPage = pagesToCheck.find((p) => dashboard.canAddTextZoneToPage(p));
		return dashboard.addTextZoneToPage(addToPage);
	});

	return {
		canAddTextZoneToDefinition,
		canAddTextZoneToSpread,
		addTextZone,
	};
}
